import { useMemo, useState } from 'react'
import { Modal, Dropdown } from 'antd'
import { AiAgentLogo } from './AiAgentLogo'
import { AiAgentMessage, aiAgentRoles } from '../ai-agent.type'
import { timeAgoOrTime, shouldShowTimestamp } from '../libs/message-time.utils'
import { handleCopyContent, copyTableToClipboard } from '../libs/clipboard.utils'
import { useChatContext } from '../contexts/useChatContext'
import { CopyIcon, ExportIcon, EmailIcon } from '../assets/svgs'
import { exportElementTablesToCsv } from '../libs/csv-export'

interface AiAgentMessageBubbleProps {
  msg: AiAgentMessage
  idx: number
  messages: AiAgentMessage[]
  showTimestamp: boolean
  isInitial?: boolean
  onRegenerate?: (messageId: string) => void
  onDownloadPdf?: (args: { html: string; frontMatter: string; format?: 'Pdf' | 'Docx' }) => void
  hasTwoPoliciesSelected?: boolean
  onOpenEmail?: ({ html, subject }: { html?: string; subject?: string }) => void
}

function AiAgentMessageBubble({
  msg,
  idx,
  messages,
  showTimestamp,
  isInitial,
  onRegenerate,
  onDownloadPdf,
  hasTwoPoliciesSelected = false,
  onOpenEmail,
}: AiAgentMessageBubbleProps) {
  const {
    openPolicyPdf,
    loadComparisonByPolicyCodes,
    getFeatureIdsFromText,
    applyFeatureFiltersByIds,
    getSelectedPolicyContents,
  } = useChatContext()

  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)

  const confirmAndLoad = (codes: string[]) => {
    Modal.confirm({
      icon: null,
      title: 'Load comparison?',
      content:
        'This will clear your current selections and load the policies referenced in this message.',
      okText: 'Load',
      cancelText: 'Cancel',
      onOk: () => loadComparisonByPolicyCodes(codes),
    })
  }

  // Handle clicks on bubble content
  const handleBubbleClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement

    if (target.classList.contains('policy-page-link')) {
      event.preventDefault()
      const pageNumber = target.getAttribute('data-text')
      const pdfPath = target.getAttribute('data-pdf-path')
      const policyCode = target.getAttribute('data-policy-code')
      openPolicyPdf(pdfPath, policyCode, pageNumber)
      return
    }

    if (target.classList.contains('load-comparison-link')) {
      event.preventDefault()
      const codesAttr = target.getAttribute('data-policy-codes') || ''
      const codes = codesAttr
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean)
      if (codes.length && getSelectedPolicyContents().length) {
        confirmAndLoad(codes)
      }
      if (codes.length && !getSelectedPolicyContents().length) {
        loadComparisonByPolicyCodes(codes)
      }
    }

    // Table action buttons inside rendered HTML
    const action = target.getAttribute('data-action')
    if (action === 'copy-table') {
      event.preventDefault()
      const tableId = target.getAttribute('data-table-id')
      if (tableId) {
        await copyTableToClipboard(tableId, target)
      }
      return
    }

    if (action === 'export-csv') {
      event.preventDefault()
      // Export all tables in this message bubble
      const bubble = event.currentTarget as HTMLElement
      exportElementTablesToCsv(bubble, {
        includeMessageId: msg.id,
        filenamePrefix: 'al-response',
      })
      return
    }
  }

  // Derive policy codes referenced in the bubble and feature IDs from message text
  const referencedPolicyCodes = useMemo(() => {
    const html = msg.parsedHtml || ''
    const regex = /class="policy-page-link"[^>]+data-policy-code="([^"]+)"/gi
    const codes: string[] = []
    let m: RegExpExecArray | null
    while ((m = regex.exec(html)) !== null) {
      const code = m[1]
      if (code && !codes.includes(code)) codes.push(code)
    }
    return codes
  }, [msg.parsedHtml])

  const referencedFeatureIds = useMemo(() => {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = msg.parsedHtml || msg.text || ''
    const plainText = tempDiv.textContent || tempDiv.innerText || ''
    return getFeatureIdsFromText(plainText)
  }, [msg.parsedHtml, msg.text, getFeatureIdsFromText])

  const hasTables = useMemo(() => {
    const html = msg.parsedHtml || ''
    if (!html) return false
    return /<table[\s>]/i.test(html)
  }, [msg.parsedHtml])

  // Identify "key differences" bubble: content contains exactly one table and no other block content
  const isSingleTableOnly = useMemo(() => {
    const html = msg.parsedHtml || ''
    if (!html) return false
    const container = document.createElement('div')
    container.innerHTML = html

    const tables = Array.from(container.querySelectorAll('table'))
    if (tables.length !== 1) return false

    // No additional content elements besides table-related containers/actions
    const otherBlocks = container.querySelectorAll('p, h1, h2, h3, ul, ol, blockquote, pre')
    if (otherBlocks.length > 0) return false

    return true
  }, [msg.parsedHtml])

  // Parser now sets --ai-cols per table; no post-render balancing required

  // Handle regenerate action
  const handleRegenerate = () => {
    if (onRegenerate) {
      onRegenerate(msg.id)
    }
  }

  // Only show actions for assistant messages
  const shouldShowActions = msg.sender === aiAgentRoles.assistant

  const handleExport = (format: 'Pdf' | 'Docx') => {
    if (!onDownloadPdf || isDownloadingPdf) return
    setIsDownloadingPdf(true)
    try {
      const result = onDownloadPdf({
        html: msg.parsedHtml,
        frontMatter: '',
        format,
      })
      Promise.resolve(result).finally(() => setIsDownloadingPdf(false))
    } catch {
      setIsDownloadingPdf(false)
    }
  }

  return (
    <div className={`ai-agent-msg ai-agent-msg--${msg.sender}`}>
      {msg.sender === 'assistant' && (
        <AiAgentLogo className='ai-agent-avatar-small' iconType='head' size={22} />
      )}
      <div
        className={`ai-agent-bubble ai-agent-bubble-animate-in`}
        data-message-id={msg.id}
        onClick={handleBubbleClick}
      >
        <div className='ai-agent-bubble-content'>
          <div
            dangerouslySetInnerHTML={{
              __html: msg.sender === 'assistant' ? msg.parsedHtml || msg.text : msg.text,
            }}
          />
        </div>

        {/* Message Actions */}
        {shouldShowActions && !isInitial && (
          <div className='ai-agent-message-actions'>
            {referencedPolicyCodes.length >= 1 && (
              <button
                className='ai-agent-action-btn'
                onClick={(e) => {
                  e.preventDefault()
                  if (referencedPolicyCodes.length) {
                    if (getSelectedPolicyContents().length) confirmAndLoad(referencedPolicyCodes)
                    else loadComparisonByPolicyCodes(referencedPolicyCodes)
                  }
                }}
                title={`Load policy ${referencedPolicyCodes
                  .map((code) => code)
                  .join(', ')} to comparison from this message`}
                aria-label={`Load (${referencedPolicyCodes.length}) policies to comparison from this message`}
              >
                <span className='ai-agent-action-icon'>🖺</span>
                <span className='ai-agent-action-text'>
                  Load {referencedPolicyCodes.length}{' '}
                  {referencedPolicyCodes.length > 1 ? 'policies' : 'policy'}
                </span>
              </button>
            )}
            {referencedFeatureIds.length > 0 && false && (
              <button
                className='ai-agent-action-btn'
                onClick={(e) => {
                  e.preventDefault()
                  applyFeatureFiltersByIds(referencedFeatureIds)
                }}
                title={`Apply filters (${referencedFeatureIds.length})`}
                aria-label='Apply feature filters from this message'
              >
                <span className='ai-agent-action-icon'>✨</span>
                <span className='ai-agent-action-text'>Apply Features</span>
              </button>
            )}
            <button
              className='ai-agent-action-btn ai-agent-action-btn--copy'
              onClick={() => handleCopyContent(msg)}
              title='Copy message content'
              aria-label='Copy message content'
            >
              <CopyIcon className='ai-agent-action-icon' size={14} />
              <span className='ai-agent-action-text'>Copy</span>
            </button>

            {onOpenEmail && 1 !== 1 && (
              <button
                className='ai-agent-action-btn'
                onClick={(e) => {
                  e.preventDefault()
                  const html = msg.parsedHtml || ''
                  const subject = hasTables ? 'AL Comparison' : 'AL Response'
                  onOpenEmail({ html, subject })
                }}
                title='Email this response'
                aria-label='Email this response'
              >
                <EmailIcon className='ai-agent-action-icon' />
                <span className='ai-agent-action-text'>Email</span>
              </button>
            )}

            {(isSingleTableOnly || (hasTwoPoliciesSelected && hasTables)) && onDownloadPdf && (
              <Dropdown
                menu={{
                  items: [
                    { key: 'Pdf', label: 'PDF' },
                    { key: 'Docx', label: 'Word' },
                  ],
                  onClick: ({ key }) => handleExport(key as 'Pdf' | 'Docx'),
                }}
                trigger={['click']}
                placement='bottomRight'
                overlayClassName='ai-agent-export-dropdown'
                arrow={{ pointAtCenter: true }}
                disabled={!onDownloadPdf || isDownloadingPdf}
              >
                <button
                  className='ai-agent-action-btn ai-agent-action-btn--export'
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                  disabled={!onDownloadPdf || isDownloadingPdf}
                  title='Export comparison report'
                  aria-label='Export comparison report'
                >
                  <ExportIcon className='ai-agent-action-icon' />
                  <span className='ai-agent-action-text'>Report</span>
                </button>
              </Dropdown>
            )}

            {hasTables && (
              <button
                className='ai-agent-action-btn ai-agent-action-btn--export-csv'
                onClick={(e) => {
                  e.preventDefault()
                  const bubble = document.querySelector(
                    `.ai-agent-bubble[data-message-id="${msg.id}"]`,
                  ) as HTMLElement | null
                  if (!bubble) return
                  exportElementTablesToCsv(bubble, {
                    heading: 'Comparison Feature Export',
                    description:
                      'This comparison is for reference only. Please review the contents using professional judgement.',
                    includeMessageId: msg.id,
                    filenamePrefix: 'ai-agent-response',
                  })
                }}
                title='Export tables in this message as CSV'
                aria-label='Export tables in this message as CSV'
              >
                <ExportIcon className='ai-agent-action-icon' />
                <span className='ai-agent-action-text'>Export CSV</span>
              </button>
            )}

            {onRegenerate && (
              <button
                className='ai-agent-action-btn ai-agent-action-btn--regenerate'
                onClick={handleRegenerate}
                title='Regenerate this response'
                aria-label='Regenerate this response'
              >
                <span className='ai-agent-action-icon'>↻</span>
                <span className='ai-agent-action-text'>Try again</span>
              </button>
            )}
          </div>
        )}
      </div>

      {showTimestamp && msg.timestamp && shouldShowTimestamp(messages, idx) && (
        <span className='ai-agent-bubble-timestamp'>{timeAgoOrTime(msg.timestamp)}</span>
      )}
    </div>
  )
}

export { AiAgentMessageBubble }
